import { Tooltip as TooltipPrimitive } from "radix-ui";
import * as React from "react";

import { cn } from "@/utils";

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
	return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

type TooltipTriggerMode = "hover" | "click";

const TooltipCtx = React.createContext<{
	mode: TooltipTriggerMode;
	open: boolean;
	setOpen: (next: boolean) => void;
} | null>(null);

function Tooltip({
	trigger = "hover",
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Root> & { trigger?: TooltipTriggerMode }) {
	const [open, setOpen] = React.useState(false);
	const isClick = trigger === "click";

	return (
		<TooltipProvider>
			<TooltipCtx.Provider value={{ mode: trigger, open, setOpen }}>
				<TooltipPrimitive.Root
					data-slot="tooltip"
					{...props}
					// 在点击模式下使用受控方式，忽略悬停触发
					open={isClick ? open : props.open}
					onOpenChange={isClick ? () => {} : props.onOpenChange}
				/>
			</TooltipCtx.Provider>
		</TooltipProvider>
	);
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
	const ctx = React.useContext(TooltipCtx);

	// 使用与 Trigger onClick 参数一致的事件类型，避免类型不匹配
	type TriggerProps = React.ComponentProps<typeof TooltipPrimitive.Trigger>;
	type ClickEvent = Parameters<NonNullable<TriggerProps["onClick"]>>[0];

	// 普通函数，避免将 props 作为 hook 依赖
	const handleClick = (e: ClickEvent) => {
		// 保留用户自定义 onClick
		props.onClick?.(e);
		if (ctx && ctx.mode === "click") {
			ctx.setOpen(!ctx.open);
		}
	};

	return (
		<TooltipPrimitive.Trigger
			data-slot="tooltip-trigger"
			{...props}
			onClick={ctx?.mode === "click" ? handleClick : props.onClick}
		/>
	);
}

function TooltipContent({
	className,
	sideOffset = 4,
	side = "top",
	children,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
	const ctx = React.useContext(TooltipCtx);

	const closeIfClickMode = React.useCallback(() => {
		if (ctx && ctx.mode === "click") {
			ctx.setOpen(false);
		}
	}, [ctx]);

	return (
		<TooltipPrimitive.Portal>
			<TooltipPrimitive.Content
				data-slot="tooltip-content"
				side={side}
				sideOffset={sideOffset}
				className={cn(
					"bg-popover-foreground text-popover animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
					className,
				)}
				// 点击外部或按下 Esc 关闭（仅点击模式下）
				onPointerDownOutside={ctx?.mode === "click" ? closeIfClickMode : props.onPointerDownOutside}
				onEscapeKeyDown={ctx?.mode === "click" ? closeIfClickMode : props.onEscapeKeyDown}
				{...props}
			>
				{children}
				<TooltipPrimitive.Arrow className="bg-popover-foreground fill-popover-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
			</TooltipPrimitive.Content>
		</TooltipPrimitive.Portal>
	);
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
